import { Button } from '@optools/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { Table } from '@tanstack/react-table';
import { ChevronLeftIcon, ChevronRightIcon, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
}

export function DataTablePagination<TData>({ table }: DataTablePaginationProps<TData>) {
  const currentPage = table.getState().pagination.pageIndex + 1;
  const itemsPerPage = table.getState().pagination.pageSize;
  const totalCount = table.getRowCount();

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  return (
    <div className="flex items-center justify-end">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
            }}>
            <SelectTrigger aria-label="Rows Per Page" className="w-18.5">
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 25, 50, 100].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-24 items-center justify-center text-sm font-medium whitespace-nowrap">
          {startItem}-{endItem} of {totalCount}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            className="hidden lg:flex"
            disabled={!table.getCanPreviousPage()}
            size="icon"
            variant="outline"
            onClick={() => table.setPageIndex(0)}>
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </Button>
          <Button
            disabled={!table.getCanPreviousPage()}
            size="icon"
            variant="outline"
            onClick={() => table.previousPage()}>
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon />
          </Button>
          <Button
            disabled={!table.getCanNextPage()}
            size="icon"
            variant="outline"
            onClick={() => table.nextPage()}>
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon />
          </Button>
          <Button
            className="hidden lg:flex"
            disabled={!table.getCanNextPage()}
            size="icon"
            variant="outline"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}>
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
